// Level guides — reference material for the real-world topic behind each
// level, independent of game mechanics. Rendered by GuideModal.jsx, opened
// from the "📖 Guide" button on each Dashboard level card.
//
// Schema per section: { heading, body?: string, list?: string[] }
// `body` is one or two short paragraphs; `list` is a bullet set. A section
// can use either or both.

export const GUIDES = {
  "budget-commander": {
    title: "Budget Commander",
    tagline: "Field Manual: Zero-Based Budgeting",
    sections: [
      {
        heading: "The Core Idea",
        body: "Zero-based budgeting means giving every dollar of income a job before the month starts. Income minus every planned category should land on exactly $0 — not because you spend it all, but because even your savings and debt payoff are \"jobs\" a dollar can have. Money without an assigned job is the money that quietly disappears.",
      },
      {
        heading: "Fixed vs. Variable",
        body: "Fixed expenses cost the same every month — rent, a gym membership, a phone plan. Variable expenses move around — groceries, gas, utilities. Knowing which is which tells you where a budget actually has room to flex when money's tight, and where it doesn't.",
      },
      {
        heading: "Needs vs. Wants",
        body: "A Need keeps the lights on and the fridge stocked. A Want makes life better but isn't required to function. Neither label is a judgment call about the purchase — the point is just knowing which is which before a shortfall forces the choice for you.",
      },
      {
        heading: "The Big Three, Beyond Essentials",
        list: [
          "Emergency Fund — cash set aside for the unplanned, like a car repair or a medical bill. A common real-world target is 3–6 months of essential expenses.",
          "Debt Payoff — payments above the minimum. Every extra dollar here stops interest from accruing on it, permanently.",
          "Sinking Fund — small amounts saved now for an expense you already know is coming later, like an annual renewal or a holiday.",
        ],
      },
      {
        heading: "When the Plan Runs Over Budget",
        body: "Trim Discretionary and Lifestyle spending first — they're built to flex. Essentials and the Big Three exist specifically so they don't have to be the first thing cut when a month gets tight.",
      },
    ],
  },

  "time-raid": {
    title: "Time Raid",
    tagline: "Field Manual: The Eisenhower Matrix",
    sections: [
      {
        heading: "Two Questions, Four Quadrants",
        body: "Every task can be sorted along two axes: is it urgent, and is it important? That produces four quadrants, and almost everything competing for your attention falls into one of them.",
        list: [
          "Q1 — Do First: Urgent + Important. Crises and real deadlines. Necessary, but living here constantly is a sign of poor planning, not high performance.",
          "Q2 — Schedule: Important, Not Urgent. Studying, exercise, planning ahead. Nothing forces you here, which is exactly why it has to be deliberately protected — this is where long-term results actually come from.",
          "Q3 — Delegate or Decline: Urgent, Not Important. Other people's priorities showing up as your interruptions. Worth a polite no or a handoff, not automatic yes.",
          "Q4 — Cut: Neither urgent nor important. Time-fillers. Fine in small, chosen doses — a problem when they eat a block that was meant for something else.",
        ],
      },
      {
        heading: "\"Eat the Frog\"",
        body: "Do your single hardest, highest-impact task first, before the day has a chance to fill up with smaller, easier things. Everything after that feels lighter by comparison — and it's already handled.",
      },
      {
        heading: "Small Tools That Add Up",
        list: [
          "The 2-Minute Rule — if it takes under two minutes, do it now instead of letting it join a growing list.",
          "The 5-Second Rule — count down and physically start before hesitation gets a vote.",
          "Kaizen — one small step is still progress, especially on the days a big leap feels impossible.",
          "Batching — group similar small tasks (errands, quick replies) into one pass instead of constant context-switching.",
        ],
      },
      {
        heading: "Protecting the Block",
        body: "A focused block only works if it's actually protected — notifications off, one task in view, a visible timer to fight the sense that time just \"got away.\" A calm, direct \"not now\" to a distraction is a completed skill, not a rude one. And a genuine open buffer in the schedule is what keeps a real emergency from knocking everything else over.",
      },
    ],
  },

  "compound-quest": {
    title: "Compound Quest",
    tagline: "Field Manual: Compound Interest & Investing Basics",
    sections: [
      {
        heading: "Compound Interest, In One Line",
        body: "Compound interest is earning returns on your returns, not just on your original money. The earlier a dollar starts compounding, the more years it has to snowball — which is why time in the market tends to matter more than the size of any single contribution.",
      },
      {
        heading: "A Quick Way to Estimate It: The Rule of 72",
        body: "Divide 72 by an annual return rate to roughly estimate how many years it takes an investment to double. At 9% a year, that's about 8 years to double — a fast mental gut-check, not exact math.",
      },
      {
        heading: "What You're Comparing When You Pick an Investment",
        list: [
          "Risk — how much the value could swing, and how likely a real, permanent loss is (not just a temporary dip).",
          "Fees (expense ratio) — a small-looking percentage charged every single year, which compounds against you the same way returns compound for you.",
          "Diversification — spreading money across many companies instead of betting everything on one. A total-market index fund, for example, owns hundreds of companies at once, so no single failure sinks it.",
        ],
      },
      {
        heading: "Why Fees Matter More Than They Look",
        body: "A fund charging 1.2% a year sounds small, but subtracted every year for decades, it meaningfully shrinks the final total — often by more than most people expect. Most professionally \"actively managed\" funds still don't outperform a plain, low-cost index fund over the long run once fees are factored in.",
      },
      {
        heading: "Staying Invested Through Drops",
        body: "Markets dip — sometimes sharply. Selling after a drop turns a temporary, paper loss into a permanent, real one, and it locks you out of the recovery that historically follows. Continuing to invest steadily through a downturn (dollar-cost averaging) actually buys more shares while prices are lower.",
      },
    ],
  },

  "property-tycoon": {
    title: "Property Tycoon",
    tagline: "Field Manual: Real Estate Strategies",
    sections: [
      {
        heading: "Three Ways In",
        list: [
          "Land Flipping — buy overlooked or undervalued land from a motivated seller, then resell it or offer owner financing. Deals like this typically only work at a steep discount to market value.",
          "Rental Property — buy a property where market rent covers the mortgage, taxes, insurance, and maintenance, with room left over as monthly cash flow.",
          "House Hacking — buy a multi-unit property, live in one unit, and let a tenant's rent cover most (or all) of the mortgage on the others.",
        ],
      },
      {
        heading: "Financing, at a Glance",
        list: [
          "Low-Down (FHA) — as little as 3.5% down, but generally requires living in the property as an owner-occupant for at least a year.",
          "Standard Down — a larger down payment (commonly 15–20%), typical for a straightforward rental purchase.",
          "Cash / Creative — buying outright, or structuring seller financing directly with the seller — common in smaller land deals.",
        ],
      },
      {
        heading: "Red Flags Worth Walking Away From",
        body: "High HOA dues paired with rental restrictions, major deferred repairs the seller won't credit for, and a price that's already right at fair market value with no urgency behind the sale — none of these are automatically disqualifying, but each one erases margin a beginner usually can't afford to lose.",
      },
      {
        heading: "Due Diligence Habits",
        body: "A professional inspection before closing, verifying legal road access and flood-zone status through county records, and shopping financing with a soft-pull pre-approval before applying anywhere formally — these are unglamorous steps that catch expensive surprises before they become your problem instead of the seller's.",
      },
      {
        heading: "The One-Year Rule for House Hacking",
        body: "An owner-occupant loan (like FHA) requires actually living in the property for a minimum period, commonly a year. Moving out early can create a real compliance problem with the lender — plan any move around that window, not around it.",
      },
    ],
  },

  "bond-bastion": {
    title: "Bond Bastion",
    tagline: "Field Manual: How Bonds Work",
    sections: [
      {
        heading: "What a Bond Actually Is",
        body: "A bond is a loan — you lend money to a government or a company, they pay you a fixed interest rate (the coupon) on a schedule, and they return your original principal when the bond matures. It's fundamentally different from a stock: you're a lender, not an owner.",
      },
      {
        heading: "The Golden Rule of Bonds",
        body: "When interest rates rise, the price of existing bonds falls — because a new bond issued today pays more than an old one issued yesterday, so the old one becomes less attractive and worth less on paper. This only matters if you sell before maturity; hold a bond to maturity and you still get your full principal back, barring default.",
      },
      {
        heading: "Duration: Interest-Rate Risk",
        body: "Duration measures how sensitive a bond's price is to interest rate changes. Longer-term bonds (a 30-year Treasury) swing harder when rates move; shorter-term bonds (a 6-month T-Bill) barely move at all. Longer duration means more risk, but historically also a bit more yield to compensate.",
      },
      {
        heading: "Credit Rating: Default Risk",
        body: "A credit rating estimates how likely an issuer is to actually pay you back. AAA (like U.S. Treasuries) is the safest tier; \"junk\" or high-yield bonds sit at the riskiest end. A junk bond's higher coupon isn't free money — it's compensation for a real chance the issuer can't pay it all back.",
      },
      {
        heading: "Two Structures",
        list: [
          "Individual bonds — held to maturity, full principal returns barring default. Price dips along the way are just paper, as long as you don't sell.",
          "Bond funds — a managed basket with no single maturity date, constantly trading to hold a target duration. More convenient, but exposed to rate changes differently than a bond you're holding to term.",
        ],
      },
      {
        heading: "Inflation Protection: TIPS",
        body: "Treasury Inflation-Protected Securities pay a lower coupon in exchange for a principal that adjusts upward with inflation — a different kind of insurance than chasing a higher headline rate.",
      },
    ],
  },

  "wildcat-portfolio": {
    title: "Wildcat Portfolio",
    tagline: "Field Manual: Alternative Assets",
    sections: [
      {
        heading: "Build the Core Before the Satellite",
        body: "Alternative assets are meant to sit alongside a solid financial foundation, not replace one. Common prerequisites before adding them: being debt-free (or actively paying down high-interest debt), a fully funded emergency fund of 3–6 months of expenses, and stable income. Skipping straight to alt-assets without that foundation turns an edge into a real vulnerability.",
      },
      {
        heading: "Five Categories, Five Trade-offs",
        list: [
          "Real Estate — rental income and long-term appreciation, but low liquidity; selling typically takes 30+ days.",
          "Gold & Silver — a historical hedge against inflation and currency risk, moderate liquidity, not free of ups and downs.",
          "Cryptocurrency — very high volatility, but also very high liquidity, trading 24/7. Easy to buy or sell any time — which is exactly what makes panic-selling so tempting when it drops.",
          "Business & Skills — starting a venture, or investing in your own earning power. High potential, low liquidity — hard to cash out on short notice, but one of the most powerful levers there is.",
          "Collectibles — art, cards, and similar niche items. Accessible, but returns aren't predictable the way a broad market index is, and finding a fair-price buyer can take patience.",
        ],
      },
      {
        heading: "Position Sizing Is the Real Skill",
        body: "The specific percentage matters less than the discipline of capping any single volatile position on purpose, before emotion is involved — deciding a limit while calm is very different from deciding one mid-swing.",
      },
      {
        heading: "\"Good Debt\"",
        body: "Not all debt is the same. Borrowing to acquire something that generates more income than the debt costs — a rental property that cash-flows more than its loan payment, for instance — is a fundamentally different bet than borrowing to consume. The test is simple: does the asset pay for the liability, with room to spare?",
      },
    ],
  },

  "off-the-clock": {
    title: "Off The Clock",
    tagline: "Field Manual: Building Leverage With Your Hours",
    sections: [
      {
        heading: "Four Kinds of Leverage",
        list: [
          "Information — knowledge you've already organized that other people keep asking for.",
          "Systems — a repeatable process that removes a recurring point of friction, for you or for others.",
          "People — relationships and trust, built over time, that open doors a resume alone can't.",
          "Capital — money put to work. Usually the last domino to fall, not the first — it works best amplifying something that's already proven to work.",
        ],
      },
      {
        heading: "Spotting a Real Pattern",
        body: "A one-off annoyance and a real opportunity look similar in the moment. The difference is repetition: does this keep happening, to you or to people around you? A single bad day isn't a pattern. Three people asking for the same thing in one week usually is.",
      },
      {
        heading: "Sharp Beats Vague",
        body: "\"I want to make some extra money somehow\" doesn't point anywhere. \"Turn my study guides into a shared notes library within 30 days\" does. A goal with a specific target and a date is something you can actually act on tomorrow morning — a vague one just sits there feeling motivational.",
      },
      {
        heading: "Ship Before It's Perfect",
        body: "A rough, genuinely useful version in front of real people teaches you more in a week than solo polishing teaches in a month — because real feedback tells you exactly what to fix, which guessing alone never does.",
      },
      {
        heading: "Usefulness Opens Doors",
        body: "Walking into a relationship asking what the other person needs, before asking for anything yourself, is what actually builds trust and opportunity over time. It isn't a trick — it's just how real leverage with people gets built.",
      },
    ],
  },

  "inner-citadel": {
    title: "The Inner Citadel",
    tagline: "Field Manual: Stoic Resilience",
    sections: [
      {
        heading: "The Dichotomy of Control",
        body: "A core idea from Stoic philosophy (Epictetus, specifically): some things are within your control — your effort, your choices, how you respond. Almost everything else — other people's opinions, a grade already turned in, the weather — is not. Nearly everything that genuinely disturbs people falls in the second category, and nearly everything that actually helps falls in the first. Sorting a situation into the right bucket, quickly, is most of the skill.",
      },
      {
        heading: "The Gap Between Stimulus and Response",
        body: "Something happens, and then — separately — you react to it. That gap is small, but it's real, and it's trainable. A rude comment or a bad grade is the stimulus; snapping back immediately or spiraling for the rest of the day is a choice happening inside that gap, even when it doesn't feel like one in the moment.",
      },
      {
        heading: "Systems Beat Willpower",
        body: "A goal is a destination. A system is what actually moves you there on the days you don't feel like it. A handful of small daily practices, done consistently, outlasts a long list of good intentions attempted once and abandoned.",
      },
      {
        heading: "Asking for Help Is a Strength",
        body: "Carrying something heavy alone, silently, isn't the same as handling it well — it's just handling it invisibly, usually at a higher cost than it needed to. Saying what's actually going on to a trusted person is one of the fastest ways to make a heavy week lighter.",
      },
    ],
  },

  "signal-boost": {
    title: "Signal Boost",
    tagline: "Field Manual: Professional Communication",
    sections: [
      {
        heading: "Visual vs. Vocal Image",
        body: "Listeners form initial assumptions about you from what they see — posture, dress, expression. But they form actual beliefs about your credibility and trustworthiness from what they hear. Vocal variety can feel \"fake\" the first few times you try it — that's just unfamiliarity, not inauthenticity.",
      },
      {
        heading: "The Five Vocal Dials",
        list: [
          "Rate — slow down to highlight what matters most; speed up through the parts that don't.",
          "Volume — aim for roughly a 6–7 out of 10. It's the baseline that reads as confidence, not a request to be heard.",
          "Pitch & Melody — break the flat \"Monday melody\" by using your full range instead of one note.",
          "Tonality — the emotion underneath the voice. Your face is the remote control for it; a blank face makes a flat voice, even with all the right words.",
          "The Pause — silence gives a listener time to process, and it reads as authority, not hesitation.",
        ],
      },
      {
        heading: "Four Frameworks for Distilling a Message",
        list: [
          "PARA — Point, Action, Result, Ask. A tight structure for a status update.",
          "PAR — Pause, Ask, Respond. For handling a pressure question: think first, find the real question behind it, then answer that.",
          "3-2-1 — three steps, two types, one thing. The constraint of naming \"the one thing\" forces a distilled answer instead of a ramble.",
          "AMS — Analogy, Metaphor, Simile. Explain something complex by comparing it to something the listener already understands.",
        ],
      },
      {
        heading: "Habits Worth Fixing",
        list: [
          "Filler words — replace \"um\" and \"uh\" with a silent pause instead.",
          "Softeners — \"this might be a bad idea, but...\" Say the direct version instead.",
          "Rushing — nerves speed people up. Consciously slowing down signals safety to your own nervous system, and to the room.",
          "Backstory overload — repeat the actual question back, then answer it directly, instead of leading with all the context first.",
          "Interrupting — wait two full seconds after someone finishes before replying.",
          "Main character syndrome — aim for roughly three questions asked for every one story told about yourself.",
        ],
      },
      {
        heading: "High-Stakes Situations",
        body: "In a negotiation, the goal isn't to \"win\" at the other side's expense — it's a deal both people would make again. On a video call, framing yourself from the waist up (so your hands are visible) and looking into the lens simulates real eye contact. When you genuinely don't know an answer, saying so plainly and taking it offline preserves more credibility than a confident-sounding guess ever does.",
      },
    ],
  },

  "moat-runner": {
    title: "Moat Runner",
    tagline: "Field Manual: Boring Businesses & the AI Wealth Wave",
    sections: [
      {
        heading: "Invisibility Is a Strength",
        body: "A large share of stable, high-margin businesses are \"psychologically repellent\" — unglamorous work most people won't even consider, like backflow testing, kitchen hood cleaning, or headstone restoration. Almost nobody competes for a business they'd be embarrassed to describe at a party, which is exactly why the ones who do face so little competition.",
      },
      {
        heading: "Five Moats Worth Recognizing",
        list: [
          "Regulatory Moat — a legal or code requirement (fire code, HIPAA, an elevator inspection deadline) that makes a service renewal non-negotiable, not optional.",
          "Recurring Contract — getting paid every month without a new sales pitch each time, like a valet-trash contract or a multi-year cell-tower lease.",
          "Paid Twice — getting paid to remove something, then paid again selling it as a commodity — grease-trap pumping resold to a biofuel refinery is the classic example.",
          "Hard Door — a barrier like specialized licensing or heavy equipment that most people won't bother clearing, so few competitors ever show up on the other side.",
          "Invisible Edge — work so unglamorous that it just doesn't attract competition, regardless of how profitable it actually is.",
        ],
      },
      {
        heading: "Recurring Revenue Beats a One-Time Sale",
        body: "A single sale pays once. A contract, a lease, or a compliance-driven renewal pays again and again for the same relationship — that difference compounds over years in a way constant one-off selling never does.",
      },
      {
        heading: "The Fifth Industrial Revolution",
        body: "Major tech shifts (1900, 1950, 1995, and now AI) tend to mint a new wave of wealth — but this one is moving faster than the internet's decade-long ramp-up, and the early-mover window is generally estimated to close somewhere around 2027–2030.",
      },
      {
        heading: "Three Layers to Engage the AI Wave",
        list: [
          "Layer 1 — Infrastructure: the \"shovel sellers\" (semiconductors, data centers, cooling, cybersecurity) who profit no matter which AI company ends up winning.",
          "Layer 2 — Business Implementation: helping the roughly 44% of businesses that haven't adopted AI yet actually put it to use, cutting costs or building a real edge.",
          "Layer 3 — Career Shielding: AI likely won't replace most workers soon, but workers who use it are already outpacing the ones who don't — a productivity gain estimated as high as 40%.",
        ],
      },
      {
        heading: "Low-Cost Entry Points",
        body: "None of this requires big capital to start. Mobile notary services, headstone cleaning, and mobile car detailing are commonly cited as businesses startable for under $2,000 — the moat comes from the model, not from how much money you put in on day one.",
      },
    ],
  },

  "the-standoff": {
    title: "The Standoff",
    tagline: "Field Manual: Negotiation",
    sections: [
      {
        heading: "It's Not About Winning",
        body: "Principled negotiation isn't about beating the other side — it's about finding a solution that satisfies both parties' real interests without wrecking the relationship. A deal that only works for one side tends to unravel, get renegotiated, or cost you the next deal with that same person.",
      },
      {
        heading: "BATNA and Walkaway Power",
        body: "Your BATNA — Best Alternative to a Negotiated Agreement — is your backup plan if this deal falls through, and it needs to exist before you sit down, not get improvised mid-conversation. The party who genuinely cares least about closing tends to hold the most leverage in the room, which is exactly what a real BATNA gives you: the ability to actually walk.",
      },
      {
        heading: "Interests, Not Positions",
        body: "A position is what someone says they want; an interest is why they want it. Two people fighting over one orange might both walk away happy once it turns out one only needed the fruit and the other only needed the peel — but nobody finds that out arguing over positions.",
      },
      {
        heading: "Six Tactics to Recognize",
        list: [
          "Anchoring — the first number spoken sets the mental reference point for everything after it, even when it's an obvious lowball or reach.",
          "Mirroring — repeating someone's last few words back as a question triggers an automatic urge in them to explain further.",
          "Labeling — \"it sounds like...\" names an emotion out loud, which quietly diminishes its grip on the room.",
          "Tactical Silence — refusing to fill an awkward pause creates pressure that often makes the other side negotiate against themselves.",
          "The Flinch — a visible reaction of surprise to a number plants doubt about it, whether or not the surprise is genuine.",
          "The Nibble — one small extra ask right at the moment of closing, banking on nobody wanting to reopen a deal they're mentally done with.",
        ],
      },
      {
        heading: "Handling Pressure and Power Imbalance",
        list: [
          "Fair Standards — settle deadlocks with objective criteria (market rate, an independent appraisal, a published policy) instead of a battle of willpower.",
          "Negotiation Jujitsu — when attacked personally, sidestep it by asking what's actually driving the concern, instead of defending yourself.",
          "Naming Dirty Tactics — calling out \"good cop, bad cop\" directly, out loud, tends to make it stop working immediately.",
          "Soft on the Person, Hard on the Problem — stay warm and appreciative toward the person while staying direct and firm about the actual issue.",
        ],
      },
      {
        heading: "Trading, Not Giving",
        body: "A concession offered for free teaches the other side that your position moves under pressure alone. The same concession offered conditionally — \"if I can do X, would you agree to Y?\" — keeps every move balanced by something coming back the other way.",
      },
    ],
  },

  "budget-blitz": {
    title: "Budget Blitz",
    tagline: "Field Manual: Fast Needs-vs-Wants-vs-Savings Calls",
    sections: [
      {
        heading: "Why Speed Matters Here",
        body: "In real life, budgeting decisions rarely arrive with time to deliberate — a subscription renews, a coworker suggests takeout, an ad shows up mid-scroll. The skill worth building isn't just knowing the definitions, it's recognizing the category fast enough that the reflexive \"sure, why not\" doesn't win by default.",
      },
      {
        heading: "The Three Buckets, Recapped",
        list: [
          "Needs — keeps the lights on and the fridge stocked: rent, groceries, utilities, insurance, transportation to work.",
          "Wants — makes life better but isn't required to function: streaming, dining out, new gear, entertainment.",
          "Savings — pays future-you: an emergency fund, retirement contributions, a sinking fund for something you already know is coming.",
        ],
      },
      {
        heading: "The Tempting Middle Ground",
        body: "A lot of items feel like they could go either way — a phone plan (Need, but the tier you pick is partly a Want), a gym membership (a Want for most people, arguably a Need if it's genuinely load-bearing for your health). The category isn't a moral judgment; it's just an honest label so a shortfall doesn't force the choice for you later.",
      },
      {
        heading: "Savings Is a Bill You Pay Yourself",
        body: "The easiest bucket to shortchange under time pressure is Savings, precisely because skipping it has no immediate consequence — nothing shuts off, no late fee shows up next week. Treating a savings transfer with the same non-negotiable weight as rent is what actually makes it happen consistently instead of only \"whatever's left over.\"",
      },
    ],
  },

  "market-pulse": {
    title: "Market Pulse",
    tagline: "Field Manual: Market Timing & Volatility",
    sections: [
      {
        heading: "Time In the Market, Not Timing the Market",
        body: "Trying to predict short-term price moves consistently is extraordinarily hard — even professionals with full-time research teams mostly fail at it. The far more reliable edge is staying invested through the noise, since missing even a handful of a market's best days (which tend to cluster right around its worst ones) can meaningfully drag down long-run returns.",
      },
      {
        heading: "Panic-Selling Turns Paper Losses Real",
        body: "A price drop only becomes a permanent loss the moment you sell. Ride it out, and a temporary dip stays exactly that — temporary — for as long as the underlying reason you bought hasn't actually changed.",
      },
      {
        heading: "Chasing a Pump Is Buying the Top",
        body: "A stock up sharply on hype alone — a viral post, a few green days, no real news — has already made its move. Buying in after the fact means paying the highest price of the run, right before the greater-fool trade runs out of fools.",
      },
      {
        heading: "What Actually Justifies Acting",
        list: [
          "A verified, durable change to the business — a real earnings beat, a confirmed loss of a major customer, verified fraud — is a reason to reconsider.",
          "A scary headline that turns out to be routine (a pre-scheduled insider sale, a sympathy dip from someone else's bad news) usually isn't.",
          "Broad market moves driven by macro news (rate changes, general sentiment) say very little about any one company's actual fundamentals.",
        ],
      },
      {
        heading: "Dollar-Cost Averaging",
        body: "Investing a fixed amount on a regular schedule, regardless of price, means you automatically buy more shares when prices are down and fewer when they're up — removing the temptation to time entries and exits by feel.",
      },
    ],
  },

  "scam-spotter": {
    title: "Scam Spotter",
    tagline: "Field Manual: Spotting Scams & Fraud",
    sections: [
      {
        heading: "Urgency Is the Tell",
        body: "Nearly every scam relies on rushing you past the moment you'd normally stop and check — a countdown, a threat, a limited-time offer. A legitimate request almost always survives you taking five minutes, or five hours, to verify it independently. If something is engineered to make waiting feel dangerous, that pressure is the red flag, not the story attached to it.",
      },
      {
        heading: "Verify Independently — Never Through the Message Itself",
        body: "If a text, email, or call claims to be your bank, the IRS, or a family member, don't use the phone number or link it gives you. Look up the real number yourself (the back of your card, the official website you already know) and contact them directly. A scam's entire structure depends on you trusting its own contact information.",
      },
      {
        heading: "Payment Methods That Should Stop You Cold",
        list: [
          "Gift cards — functionally untraceable and irreversible once the code is shared. No legitimate government agency, business, or family emergency is ever resolved by gift card.",
          "Wire transfers to a stranger — fast and essentially impossible to reverse once sent.",
          "Any request framed as secret — \"don't tell anyone\" is designed to prevent you from getting a second opinion that would expose the scam.",
        ],
      },
      {
        heading: "Common Patterns Worth Recognizing",
        list: [
          "Too-good-to-be-true offers — guaranteed returns, doubled money, prizes you never entered to win.",
          "Spoofed sender info — a domain that's almost right (paypa1.com), a caller ID that shows a familiar name.",
          "Authority impersonation — a caller claiming to be the IRS, a bank, or tech support, engineering fear or urgency.",
          "Romance and relationship scams — a connection that's never met in person, eventually asking for money during a conveniently unverifiable crisis.",
        ],
      },
      {
        heading: "What Legitimate Messages Actually Look Like",
        body: "Real notifications tend to be calm: a specific, expected amount; no request to click a link or reply with a code; a way to verify through a channel you already trust, independent of the message. \"No action needed\" is a very different energy than \"act now or else.\"",
      },
    ],
  },
  "cash-cascade": {
    title: "Cash Cascade",
    tagline: "Field Manual: Why Small Wins Compound",
    sections: [
      {
        heading: "This One's Just For Fun",
        body: "Cash Cascade isn't teaching a new concept — it's a break. But the way it scores actually mirrors something real: no single bumper hit matters much on its own, yet a run of small, repeatable hits adds up to a huge final number.",
      },
      {
        heading: "The Real-World Version",
        body: "That's the same shape as compound growth (see the Compound Quest guide): small, consistent, unglamorous contributions beat waiting around for one lucky shot. A single 1,000-point jackpot feels great, but most of a good score comes from just keeping the ball in play.",
      },
      {
        heading: "Scoring",
        list: [
          "Bumpers — 100 points each, every hit",
          "Targets — 250 points each; light all 3 to trigger a 1,000-point jackpot",
          "3 balls per game, final score becomes XP and net worth like any level",
        ],
      },
    ],
  },
};
